/**
 * TPRA Breach History API Request Functions & React Query Hooks
 *
 * Handles HTTP requests to the TPRA Dashboard breach history endpoint
 * with proper error handling and type safety.
 */

import { get } from '@/infra/rest';
import { APIEndpoints } from '@/shared/constants/Endpoints';
import { breachHistoryQueryKeys } from '@/shared/constants/QueryKeys';
import { addNotification } from '@/shared/states/notification';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  BreachHistoryQueryParams,
  BreachHistoryResponse,
} from '../types/breachHistory';

/**
 * Sanitizes query parameters by removing undefined/null values
 * Ensures clean API requests without empty parameters
 *
 * @param params - Raw query parameters
 * @returns Sanitized parameters object
 */
function sanitizeQueryParams(
  params: BreachHistoryQueryParams
): Record<string, string | number> {
  const sanitized: Record<string, string | number> = {};

  if (params.page !== undefined) {
    sanitized.page = params.page;
  }

  if (params.page_size !== undefined) {
    sanitized.page_size = params.page_size;
  }

  if (params.search && params.search.trim()) {
    sanitized.search = params.search.trim();
  }

  if (params.risk_level) {
    sanitized.risk_level = params.risk_level;
  }

  return sanitized;
}

/**
 * Fetches TPRA breach history list from the API
 *
 * @param params - Query parameters for filtering, pagination
 * @returns Breach history data with pagination metadata
 * @throws Error if API request fails
 *
 * @example
 * ```typescript
 * const breaches = await getBreachHistory({
 *   page: 1,
 *   page_size: 20,
 *   risk_level: 'High',
 *   search: 'google'
 * });
 * ```
 */
export async function getBreachHistory(
  params: BreachHistoryQueryParams
): Promise<BreachHistoryResponse> {
  try {
    const response = await get<BreachHistoryResponse>({
      url: APIEndpoints.TPRADashboardBreachHistory,
      config: {
        params: sanitizeQueryParams(params),
      },
    });

    return response;
  } catch (error) {
    console.error('Error fetching breach history:', error);

    addNotification({
      type: 'error',
      title: 'Failed to load breach history',
      message:
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while fetching breach history.',
    });

    throw error;
  }
}

/**
 * React Query hook for fetching TPRA breach history
 *
 * Provides:
 * - Automatic caching with TanStack Query
 * - Loading and error states
 * - Automatic refetching on mount
 * - Retry logic for failed requests
 * - Placeholder data during refetch
 *
 * @param params - Query parameters for filtering, pagination
 * @returns React Query result with breach history data
 *
 * @example
 * ```typescript
 * function BreachHistoryTable() {
 *   const { data, isLoading, isError, error } = useGetBreachHistory({
 *     page: 1,
 *     page_size: 20,
 *     risk_level: 'High'
 *   });
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (isError) return <ErrorMessage error={error} />;
 *
 *   return <Table data={data.breaches} />;
 * }
 * ```
 */
export function useGetBreachHistory(
  params: BreachHistoryQueryParams
): UseQueryResult<BreachHistoryResponse, Error> {
  return useQuery({
    queryKey: breachHistoryQueryKeys.list(params),
    queryFn: () => getBreachHistory(params),
    staleTime: 0, // Always refetch for fresh data
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchOnWindowFocus: true, // Refetch when returning to browser tab
    refetchOnMount: true,
    refetchOnReconnect: true,
    placeholderData: (previousData) => previousData,
  });
}

